// Elintra — Dashboard Shell | v3.2 | UAG v3.0.0
// FIX 1: onLogout routes to '' (LandingPage) instead of 'onboarding'.
// FIX 2: ROUTE_TITLE 'app/analytics' → 'Analytics' (canonical v3.2 name).

import { useEffect } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { OverviewPage }    from '@/pages/dashboard/OverviewPage';
import { AnalyticsPage }   from '@/pages/dashboard/AnalyticsPage';
import { AgentLogPage }    from '@/pages/dashboard/AgentLogPage';
import { StoresPage }      from '@/pages/dashboard/StoresPage';
import { ProtocolsPage }   from '@/pages/dashboard/ProtocolsPage';
import { useAppStore, useAllQueries, useQueryStats } from '@/store/appStore';
import type { Route } from '@/types/app';

// FIX: 'AEO Analytics' → 'Analytics' — canonical v3.2 name
const ROUTE_TITLE: Record<Route, string> = {
  'onboarding':    'Onboarding',
  'app':           'Overview',
  'app/analytics': 'Analytics',
  'app/log':       'Agent Log',
  'app/stores':    'Stores',
  'app/protocols': 'Protocol Health',
};

export const DashboardShell = ({
  onNavigate,
}: {
  onNavigate: (route: Route) => void;
}) => {
  const route            = useAppStore((s) => s.route);
  const user             = useAppStore((s) => s.user);
  const chartData        = useAppStore((s) => s.chartData);
  const protocolHealth   = useAppStore((s) => s.protocolHealth);
  const paymentRails     = useAppStore((s) => s.paymentRails);
  const stores           = useAppStore((s) => s.stores);
  const wsStatus         = useAppStore((s) => s.wsStatus);
  const isLiveFeedActive = useAppStore((s) => s.isLiveFeedActive);
  const isBootLoading    = useAppStore((s) => s.isBootLoading);
  const errors           = useAppStore((s) => s.errors);

  const loadAppData       = useAppStore((s) => s.loadAppData);
  const setLiveFeedActive = useAppStore((s) => s.setLiveFeedActive);
  const connectFeed       = useAppStore((s) => s.connectFeed);
  const seedQueries       = useAppStore((s) => s.seedQueries);

  const allQueries       = useAllQueries();
  const { aeoScore }     = useQueryStats();

  useEffect(() => {
    loadAppData();
    seedQueries();
    connectFeed();
  }, []);

  const handleToggleFeed = () => setLiveFeedActive(!isLiveFeedActive);

  const title = ROUTE_TITLE[route] ?? 'Dashboard';

  return (
    <DashboardLayout
      title={title}
      currentRoute={route}
      onNavigate={onNavigate}
      user={user}
      // FIX: logout goes to '' (LandingPage), not 'onboarding'
      onLogout={() => onNavigate('' as Route)}
    >
      {route === 'app' && (
        <OverviewPage
          agentQueries={allQueries}
          isLiveFeedActive={isLiveFeedActive}
          onToggleFeed={handleToggleFeed}
          wsStatus={wsStatus}
        />
      )}

      {route === 'app/analytics' && (
        <AnalyticsPage
          agentQueries={allQueries}
          chartData={chartData}
          aeoScore={aeoScore}
          isLoading={isBootLoading}
          error={errors.chart ?? null}
        />
      )}

      {route === 'app/log' && (
        <AgentLogPage
          agentQueries={allQueries}
          isLiveFeedActive={isLiveFeedActive}
          onToggleFeed={handleToggleFeed}
        />
      )}

      {route === 'app/stores' && (
        <StoresPage
          stores={stores}
          isLoading={isBootLoading}
          error={errors.stores ?? null}
        />
      )}

      {route === 'app/protocols' && (
        <ProtocolsPage
          protocolHealth={protocolHealth}
          paymentRails={paymentRails}
          isLoading={isBootLoading}
          error={errors.protocols ?? null}
        />
      )}
    </DashboardLayout>
  );
};
